/**
 * RejectReasonPanel — avvisning inline i det store kortet på Godkjenn (#155,
 * redesign steg 5a). Erstatter RejectReasonSheet (Plan 02-10 Task 3), som var
 * et BottomSheet med de samme chipene.
 *
 * Skissen: «Hvorfor? Mia ser svaret på oppgaven.», årsakschipene, og
 * Avbryt / Send tilbake. Chipene kommer fra kallstedet — oppgaver bruker
 * approvals.reject.reasons.*, bytter rewards.reject.reasons.* — og «Annet»
 * åpner fritekstfeltet på 80 tegn (ReasonChipPicker eier den grenen).
 *
 * Feil ved innsending vises her i panelet, rett over knappene, og panelet
 * står åpent så den voksne kan prøve igjen. Det er samme kontrakt som arket
 * hadde (#74 for oppgaver, #75 for bytter): feilen må stå der handlingen ble
 * gjort.
 *
 * Test-id-ene er arkets, med kallstedets prefiks (`reject-reason` for
 * oppgaver, `reward-reject` for bytter): `<prefix>-sheet`, `-submit`,
 * `-submit-spinner` og `-error`.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/primitives/Button';
import { ReasonChipPicker } from '../../components/primitives/ReasonChipPicker';
import { SheetError } from '../../components/primitives/SheetError';

interface RejectReasonPanelProps {
  /** Spørsmålet over chipene. */
  prompt: string;
  chips: string[];
  onCancel: () => void;
  onSubmit: (reason: string) => void;
  submitting?: boolean;
  /** Feilteksten fra kallstedet, vist over knappene. */
  error?: string | null;
  submitLabel: string;
  testIdPrefix: 'reject-reason' | 'reward-reject';
}

export function RejectReasonPanel({
  prompt,
  chips,
  onCancel,
  onSubmit,
  submitting = false,
  error = null,
  submitLabel,
  testIdPrefix,
}: RejectReasonPanelProps): ReactNode {
  const { t } = useTranslation();
  const [reason, setReason] = useState<string | null>(null);
  const canSubmit = reason !== null && reason.trim().length > 0 && !submitting;

  return (
    <div data-testid={`${testIdPrefix}-sheet`} className="flex flex-col gap-2">
      <p className="m-0 text-control font-extrabold text-ink">{prompt}</p>
      <ReasonChipPicker value={reason} onChange={setReason} chips={chips} />
      <SheetError message={error} data-testid={`${testIdPrefix}-error`} />
      <div className="flex gap-2">
        <Button variant="secondary" size={44} className="flex-1" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="solid"
          size={44}
          className="flex-2"
          data-testid={`${testIdPrefix}-submit`}
          disabled={!canSubmit}
          onClick={() => {
            if (reason !== null && reason.trim().length > 0) onSubmit(reason);
          }}
        >
          {submitting ? (
            <span
              data-testid={`${testIdPrefix}-submit-spinner`}
              aria-hidden
              className="inline-block h-3 w-3 animate-pulse rounded-full bg-on-solid"
            />
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </div>
  );
}
